import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../shared/api/apiClient";
import "./ContactCustomerSelect.css";

const getPrimaryValue = (rows, key) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const main = rows.find(
    (row) => String(row?.status || "").toLowerCase() === "main"
  );
  return String((main || rows[0])?.[key] || "").trim();
};

const getContactDetails = (contact) =>
  [
    getPrimaryValue(contact?.phones, "phone"),
    getPrimaryValue(contact?.emails, "email"),
  ]
    .filter(Boolean)
    .join(" · ");

function ContactCustomerSelect({ value, onChange, disabled = false }) {
  const { t } = useTranslation();
  const listboxId = useId();
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const customerName = String(value || "").trim();
  const manualCandidate = query.trim();
  const canUseManual = Boolean(manualCandidate);

  const options = useMemo(() => {
    if (
      !selectedContact ||
      contacts.some((contact) => Number(contact.id) === Number(selectedContact.id))
    ) {
      return contacts;
    }
    return [selectedContact, ...contacts];
  }, [contacts, selectedContact]);

  useEffect(() => {
    if (
      selectedContact &&
      String(selectedContact.full_name || "").trim() !== customerName
    ) {
      setSelectedContact(null);
    }
  }, [customerName, selectedContact]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setLoadError("");
      try {
        const params = { skip: 0, limit: 20 };
        if (query.trim()) params.q = query.trim();
        const { data } = await api.get("/contacts", { params });
        if (!active) return;
        setContacts(
          Array.isArray(data)
            ? data
            : Array.isArray(data?.items)
              ? data.items
              : []
        );
        setActiveIndex(-1);
      } catch (_error) {
        if (!active) return;
        setContacts([]);
        setLoadError(t("contacts_search_failed"));
      } finally {
        if (active) setLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [isOpen, query, t]);

  useEffect(() => {
    if (isOpen) window.requestAnimationFrame(() => searchRef.current?.focus());
  }, [isOpen]);

  const openSelector = () => {
    if (disabled) return;
    setQuery(selectedContact ? "" : customerName);
    setIsOpen(true);
    setActiveIndex(-1);
  };

  const chooseContact = (contact) => {
    const name = String(contact?.full_name || "").trim();
    if (!name) return;
    setSelectedContact(contact);
    setQuery("");
    setIsOpen(false);
    onChange?.(name);
  };

  const chooseManualName = () => {
    if (!canUseManual) return;
    setSelectedContact(null);
    setIsOpen(false);
    onChange?.(manualCandidate);
  };

  const clearCustomer = (event) => {
    event.stopPropagation();
    setSelectedContact(null);
    setQuery("");
    setIsOpen(true);
    onChange?.("");
  };

  const handleKeyDown = (event) => {
    const optionCount = options.length + (canUseManual ? 1 : 0);

    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!optionCount) return;
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        if (current < 0) return direction > 0 ? 0 : optionCount - 1;
        return (current + direction + optionCount) % optionCount;
      });
      return;
    }

    if (event.key !== "Enter") return;
    event.preventDefault();

    if (activeIndex >= 0 && activeIndex < options.length) {
      chooseContact(options[activeIndex]);
    } else if (canUseManual && activeIndex === options.length) {
      chooseManualName();
    } else if (options.length === 1) {
      chooseContact(options[0]);
    } else if (canUseManual && options.length === 0) {
      chooseManualName();
    }
  };

  const selectedDetails = selectedContact
    ? getContactDetails(selectedContact)
    : "";

  return (
    <div className="contactCustomerSelect" ref={rootRef}>
      <button
        type="button"
        className={`contactCustomerControl ${isOpen ? "isOpen" : ""}`}
        onClick={openSelector}
        disabled={disabled}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-haspopup="listbox"
      >
        <span className="contactCustomerIcon" aria-hidden="true">
          <i className="lni lni-user-4" />
        </span>
        <span className="contactCustomerValue">
          {customerName ? (
            <>
              <strong>{customerName}</strong>
              <small>
                {selectedDetails ||
                  (selectedContact
                    ? t("crm_contact")
                    : t("customer_name_saved"))}
              </small>
            </>
          ) : (
            <span className="contactCustomerPlaceholder">
              {t("select_crm_contact")}
            </span>
          )}
        </span>
        {customerName && !disabled && (
          <span className="contactCustomerClearSpace" aria-hidden="true" />
        )}
        <i
          className="lni lni-chevron-down contactCustomerChevron"
          aria-hidden="true"
        />
      </button>

      {customerName && !disabled && (
        <button
          type="button"
          className="contactCustomerClear"
          onClick={clearCustomer}
          title={t("clear_customer")}
          aria-label={t("clear_customer")}
        >
          ×
        </button>
      )}

      {isOpen && (
        <div className="contactCustomerDropdown">
          <div className="contactCustomerSearch">
            <i className="lni lni-search-1" aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("search_contacts")}
              aria-label={t("search_contacts")}
              aria-controls={listboxId}
              aria-activedescendant={
                activeIndex >= 0
                  ? `${listboxId}-option-${activeIndex}`
                  : undefined
              }
            />
          </div>

          <div className="contactCustomerOptions" id={listboxId} role="listbox">
            {loading && (
              <div className="contactCustomerState">
                <span className="contactCustomerSpinner" aria-hidden="true" />
                {t("loading_contacts")}
              </div>
            )}

            {!loading && loadError && (
              <div className="contactCustomerState isError">{loadError}</div>
            )}

            {!loading && !loadError && options.length === 0 && !canUseManual && (
              <div className="contactCustomerState">{t("no_contacts_found")}</div>
            )}

            {!loading &&
              options.map((contact, index) => {
                const details = getContactDetails(contact);
                return (
                  <button
                    key={contact.id}
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={
                      Number(contact.id) === Number(selectedContact?.id)
                    }
                    className={`contactCustomerOption ${
                      activeIndex === index ? "isActive" : ""
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => chooseContact(contact)}
                  >
                    <span className="contactCustomerAvatar" aria-hidden="true">
                      {String(contact.full_name || "?")
                        .trim()
                        .charAt(0)
                        .toUpperCase()}
                    </span>
                    <span className="contactCustomerOptionText">
                      <strong>
                        #{contact.ui_id || contact.id} · {contact.full_name}
                      </strong>
                      {details && <small>{details}</small>}
                    </span>
                    {Number(contact.id) === Number(selectedContact?.id) && (
                      <i className="lni lni-check" aria-hidden="true" />
                    )}
                  </button>
                );
              })}

            {!loading && canUseManual && (
              <button
                id={`${listboxId}-option-${options.length}`}
                type="button"
                role="option"
                aria-selected="false"
                className={`contactCustomerOption contactCustomerManual ${
                  activeIndex === options.length ? "isActive" : ""
                }`}
                onMouseEnter={() => setActiveIndex(options.length)}
                onClick={chooseManualName}
              >
                <span className="contactCustomerAvatar" aria-hidden="true">
                  <i className="lni lni-pencil-1" />
                </span>
                <span className="contactCustomerOptionText">
                  <strong>{t("manual_customer")}</strong>
                  <small>
                    {t("use_manual_customer_name", { name: manualCandidate })}
                  </small>
                </span>
              </button>
            )}
          </div>

          <div className="contactCustomerHint">
            {t("customer_selector_hint")}
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactCustomerSelect;
